import React, { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Create,
  SimpleForm,
  TextInput,
  ReferenceInput,
  required,
  SelectInput,
  BooleanInput,
  FormDataConsumer,
  AutocompleteInput,
  useGetOne,
  useGetList,
  minValue,
  type CreateProps,
} from "react-admin";
import { useFormContext, useWatch } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import NoArrowKeyNumberInput from "@/components/NoArrowKeyNumberInput";
import { ITSInput } from "./common/FmbContributionsItsInput";
import { BeneficiaryItsAutocomplete } from "./common/BeneficiaryItsAutocomplete";
import { formatINR } from "@/utils";
import {
  validateBeneficiaryDisplayName,
  validateContributionFmbOrPeriod,
  validatePositiveContributionTotal,
} from "./common/contribution-form-validators";
import { SeedOverrideAmountFromCalculated } from "./common/seed-override-amount-from-calculated";
import FmbZabihatBookedSlotsInput, {
  transformBookedSlotsForApi,
  validateZabihatBookedSlots,
} from "./common/FmbZabihatBookedSlotsInput";

const transform = (data: Record<string, unknown>) => {
  const out: Record<string, unknown> = {
    contributionType: data.contributionType,
    beneficiaryItsNo:
      typeof data.beneficiaryItsNo === "string"
        ? data.beneficiaryItsNo.trim()
        : data.beneficiaryItsNo,
    quantity: Number(data.quantity),
    isAmountOverridden: data.isAmountOverridden === true,
    remarks:
      typeof data.remarks === "string"
        ? data.remarks.trim() || undefined
        : (data.remarks ?? undefined),
  };
  if (data.categoryId) {
    out.categoryId = data.categoryId;
  }
  if (typeof data.beneficiaryName === "string") {
    const bn = data.beneficiaryName.trim();
    if (bn) {
      out.beneficiaryName = bn;
    }
  }

  if (data.fmbId) {
    out.fmbId = data.fmbId;
  }
  if (data.fmbTakhmeenId) {
    out.fmbTakhmeenId = data.fmbTakhmeenId;
  }
  if (data.hijriYearStart !== undefined && data.hijriYearStart !== "") {
    out.hijriYearStart = Number(data.hijriYearStart);
  }
  if (data.unitAmount !== undefined && data.unitAmount !== "") {
    out.unitAmount = Number(data.unitAmount);
  }
  if (data.isAmountOverridden && data.amount !== undefined && data.amount !== "") {
    out.amount = Number(data.amount);
  }
  const bookedSlots = transformBookedSlotsForApi(data);
  if (bookedSlots !== undefined) {
    out.bookedSlots = bookedSlots;
  }
  return out;
};

function validateContributionForm(values: Record<string, unknown>) {
  return {
    ...validateContributionFmbOrPeriod(values),
    ...validateBeneficiaryDisplayName(values),
    ...validatePositiveContributionTotal(values),
    ...validateZabihatBookedSlots(values),
  };
}

export default function FmbContributionsCreate(props: CreateProps) {
  const [searchParams] = useSearchParams();
  const prefFmbId = searchParams.get("fmbId") || "";

  const defaultValues = useMemo(
    () => ({
      fmbId: prefFmbId || undefined,
      contributionType: "ZABIHAT",
      quantity: 1,
      isAmountOverridden: false,
    }),
    [prefFmbId]
  );

  return (
    <Create {...props} transform={transform} mutationMode="pessimistic">
      <SimpleForm
        warnWhenUnsavedChanges
        sx={{ maxWidth: 760 }}
        defaultValues={defaultValues}
        validate={validateContributionForm}
      >
        <DefaultCategoryInitializer />
        <CategorySync />
        <PrefetchFmbItsDefaults prefFmbId={prefFmbId} />
        <ClearTakhmeenWhenNoFmb />
        <TakhmeenHijriSync />
        <SeedOverrideAmountFromCalculated />
        <Grid container spacing={2}>
          <Grid size={12}>
            <ReferenceInput source="fmbId" reference="fmbData" perPage={100}>
              <ITSInput
                label="FMB record (optional)"
                optionText={(r) => `${r.fileNo ?? "—"} · ITS ${r.itsNo ?? "—"}`}
                fullWidth
                debounce={300}
                filterToQuery={(q) => ({ search: q })}
                helperText={false}
              />
            </ReferenceInput>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="name" label="Account name" fullWidth disabled />
          </Grid>
          <FormDataConsumer>
            {({ formData }) =>
              formData?.fmbId ? (
                <Grid size={12}>
                  <ReferenceInput
                    source="fmbTakhmeenId"
                    reference="fmbTakhmeen"
                    filter={{ fmbId: formData.fmbId }}
                    perPage={200}
                    helperText={false}
                  >
                    <AutocompleteInput
                      fullWidth
                      label="Annual period (takhmeen)"
                      optionText={(choice) =>
                        `${choice.hijriYearStart ?? "—"}–${choice.hijriYearEnd ?? "—"} · ${formatINR(
                          choice.takhmeenAmount,
                          { empty: "—" }
                        )}`
                      }
                      shouldRenderSuggestions={(val: string) => val.trim().length >= 0}
                      noOptionsText="No annual periods for this FMB"
                      helperText={false}
                    />
                  </ReferenceInput>
                </Grid>
              ) : null
            }
          </FormDataConsumer>
          <Grid size={{ xs: 12, sm: 3 }}>
            <ReferenceInput
              source="categoryId"
              reference="fmbContributionCategory"
              filter={{ isActive: true }}
              sort={{ field: "sortOrder", order: "ASC" }}
              perPage={100}
            >
              <SelectInput
                optionText="name"
                label="Category"
                fullWidth
                validate={[required()]}
                helperText={false}
              />
            </ReferenceInput>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <NoArrowKeyNumberInput
              source="hijriYearStart"
              label="Hijri year start"
              fullWidth
              helperText="Required if no FMB record"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <BeneficiaryItsAutocomplete />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput
              source="beneficiaryName"
              label="Beneficiary name"
              fullWidth
              helperText="Auto-filled from ITS directory when available. Enter manually if ITS is not in directory."
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <NoArrowKeyNumberInput
              source="quantity"
              label="Quantity"
              fullWidth
              validate={[required(), minValue(1)]}
              helperText={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <NoArrowKeyNumberInput
              source="unitAmount"
              label="Unit amount"
              fullWidth
              helperText={false}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <CalculatedTotalAmountField />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <BooleanInput
              source="isAmountOverridden"
              label="Override total amount"
              helperText={false}
            />
          </Grid>
          <FormDataConsumer>
            {({ formData }) =>
              formData?.isAmountOverridden ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <NoArrowKeyNumberInput
                    source="amount"
                    label="Amount (override)"
                    fullWidth
                    validate={[required(), minValue(1)]}
                    helperText={false}
                  />
                </Grid>
              ) : null
            }
          </FormDataConsumer>
          <Grid size={12}>
            <TextInput source="remarks" fullWidth multiline minRows={3} />
          </Grid>
          <Grid size={12}>
            <FmbZabihatBookedSlotsInput />
          </Grid>
        </Grid>
      </SimpleForm>
    </Create>
  );
}

function ClearTakhmeenWhenNoFmb() {
  const { control, setValue } = useFormContext();
  const fmbId = useWatch({ control, name: "fmbId" });

  useEffect(() => {
    if (!fmbId) {
      setValue("fmbTakhmeenId", null, { shouldDirty: false });
    }
  }, [fmbId, setValue]);

  return null;
}

/** When a takhmeen row is chosen, align Hijri year start with that period. */
function TakhmeenHijriSync() {
  const { setValue, control } = useFormContext();
  const fmbId = useWatch({ control, name: "fmbId" });
  const fmbTakhmeenId = useWatch({ control, name: "fmbTakhmeenId" });
  const { data: t } = useGetOne(
    "fmbTakhmeen",
    { id: fmbTakhmeenId },
    { enabled: Boolean(fmbId) && Boolean(fmbTakhmeenId) }
  );

  useEffect(() => {
    if (t?.hijriYearStart != null && fmbTakhmeenId) {
      setValue("hijriYearStart", t.hijriYearStart, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [t?.hijriYearStart, t?.id, fmbTakhmeenId, setValue]);

  return null;
}

function PrefetchFmbItsDefaults({ prefFmbId }: { prefFmbId: string }) {
  const { setValue, getValues } = useFormContext();
  const { data: fmb } = useGetOne("fmbData", { id: prefFmbId }, { enabled: Boolean(prefFmbId) });
  const didPrefillRef = useRef(false);

  useEffect(() => {
    if (didPrefillRef.current) return;
    const raw = fmb?.itsNo;
    if (raw == null || raw === "") return;
    const hofIts = String(raw).trim();
    if (!hofIts) return;
    const current = getValues("beneficiaryItsNo");
    if (current != null && String(current).trim() !== "") {
      didPrefillRef.current = true;
      return;
    }
    setValue("beneficiaryItsNo", hofIts, { shouldDirty: false, shouldTouch: false });
    const accountName =
      fmb?.name != null && String(fmb.name).trim() !== "" ? String(fmb.name).trim() : "";
    const currentBn = getValues("beneficiaryName");
    if (accountName && (currentBn == null || String(currentBn).trim() === "")) {
      setValue("beneficiaryName", accountName, { shouldDirty: false, shouldTouch: false });
    }
    didPrefillRef.current = true;
  }, [fmb, prefFmbId, getValues, setValue]);

  return null;
}

function DefaultCategoryInitializer() {
  const { control, setValue } = useFormContext();
  const currentCategoryId = useWatch({ control, name: "categoryId" });
  const { data: categories } = useGetList<{
    id: string;
    code: string;
    defaultUnitAmount?: number | null;
  }>("fmbContributionCategory", {
    filter: { isActive: true },
    sort: { field: "sortOrder", order: "ASC" },
    pagination: { page: 1, perPage: 100 },
  });

  useEffect(() => {
    if (!currentCategoryId && categories && categories.length > 0) {
      const zabihat = categories.find((c) => c.code === "ZABIHAT") || categories[0];
      setValue("categoryId", zabihat.id, { shouldDirty: false });
      setValue("contributionType", zabihat.code === "ZABIHAT" ? "ZABIHAT" : "VOLUNTARY", {
        shouldDirty: false,
      });
      if (zabihat.defaultUnitAmount != null) {
        setValue("unitAmount", zabihat.defaultUnitAmount, { shouldDirty: false });
      }
    }
  }, [currentCategoryId, categories, setValue]);

  return null;
}

function CategorySync() {
  const { control, setValue } = useFormContext();
  const categoryId = useWatch({ control, name: "categoryId" });
  const prevCategoryIdRef = useRef<string | undefined>(categoryId);
  const { data: category } = useGetOne<{
    id: string;
    code: string;
    defaultUnitAmount?: number | null;
  }>("fmbContributionCategory", { id: categoryId }, { enabled: Boolean(categoryId) });

  useEffect(() => {
    if (!category) return;
    const isInitial = prevCategoryIdRef.current === undefined;
    prevCategoryIdRef.current = categoryId;

    const nextType = category.code === "ZABIHAT" ? "ZABIHAT" : "VOLUNTARY";
    setValue("contributionType", nextType, { shouldDirty: false });

    if (!isInitial && category.defaultUnitAmount != null && category.defaultUnitAmount > 0) {
      setValue("unitAmount", category.defaultUnitAmount, { shouldDirty: true });
    }
  }, [category, categoryId, setValue]);

  return null;
}

function CalculatedTotalAmountField() {
  const { control } = useFormContext();
  const quantity = useWatch({ control, name: "quantity" });
  const unitAmount = useWatch({ control, name: "unitAmount" });

  const q = Number(quantity) || 0;
  const u = Number(unitAmount) || 0;
  const total = q * u;

  return (
    <TextField
      label="Calculated total amount"
      value={total}
      fullWidth
      disabled
      inputProps={{ inputMode: "numeric" }}
    />
  );
}
